// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// Read-only current-status view (ADR-001, ADR-004 D4). Two layers under the
// device identity: production/meta and updater/execution. A conditional offline
// augment list (ADR-004 D6) renders beneath the operation when present. Persist
// via the browser print path only — NO document export (ADR-001 / README).
//
// Identity and operation fields render inside NCStatTileGrid/NCStatTile
// (nc-components/usage-notes/stat-tile-grid.md) instead of a plain label:value
// list — a device's key identifying fields is exactly this component's
// documented real-world use case, and the tile shape gives each record its
// own scannable slot instead of a dense, same-weight column of text.

import {
  AlertTriangle,
  Bluetooth,
  Cable,
  CheckCircle2,
  Circle,
  Clock,
  History,
  MinusCircle,
  UploadCloud,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";

import type {
  DeviceIdentity,
  OfflineAttributionEntry,
  OfflineBundleRecord,
  OfflineWinnerInfo,
  SelectedOperation,
} from "@/contracts/display";
import { isUnknown, type Unknownable } from "@/contracts/display";
import type { QueriedUpdaterAU } from "@/contracts/requests";
import { formatTimestamp, formatUnknownableTimestamp } from "@/lib/format-timestamp";
import { cn } from "@/lib/utils";
import { NCStatTile, NCStatTileGrid } from "@/nc/stat-tile-grid";
import { Alert, AlertDescription, AlertTitle } from "@/primitives/alert";
import { Badge } from "@/primitives/badge";
import { Card, CardContent } from "@/primitives/card";

import { Field } from "./field";

export interface DeviceViewProps {
  identity: DeviceIdentity;
  /** Present when D3/#24's winner is AU0008/AU0004-sourced (ADR-004 D2/D4). */
  operation?: SelectedOperation;
  /**
   * Present when D3/#24's winner is the AU0001 peer read instead (ADR-004 D4
   * mapping decided alongside #25/#27) — mutually exclusive with `operation`.
   * Absent from both means the device resolved but has no operation on
   * record (D1 empty result).
   */
  offlineWinner?: OfflineWinnerInfo;
  /** FirmwareDeliveryAPIv1's last-3 bundle-creation records (ADR-004 D6; #27). */
  offlineAugment?: readonly OfflineBundleRecord[];
  /**
   * AU0001's `attribution_list` — this device's fuller offline attribution
   * history (provisional local display addition, agreed 2026-08-10, pending
   * an ADR-004 amendment). Advisory only, like `offlineAugment`.
   */
  offlineAttributionList?: readonly OfflineAttributionEntry[];
  /** Updater AUs consulted during D1 collection (ADR-004 D1 O1 traceability). */
  queriedUpdaterAUs?: readonly QueriedUpdaterAU[];
}

export function DeviceView({
  identity,
  operation,
  offlineWinner,
  offlineAugment,
  offlineAttributionList,
}: DeviceViewProps) {
  return (
    // `print:block` overrides the flex column for print: a flex container's
    // own box can't fragment across print pages in Chromium (it prints as
    // one unbreakable item), so once this device's sections grow past one
    // page, the whole thing gets pushed onto the next page and the first
    // page prints blank (see the same fix in App.tsx). Plain block flow
    // paginates normally between the sections below.
    <div className="flex flex-col gap-nc-3 print:block">
      {/* Demo-registry warning (ADR-008 D1/D3): shown only for a device
          resolved from app74 — derived server-side from WHICH registry
          returned it, never client-selected. Uses the Alert primitive's
          `warning` tone (NC feedback-2 token, orange) so it reads as a
          caution banner, not a neutral label — a demo device's data is
          real-looking but not production, and that distinction matters to
          the person reading the result. Absent/unknown renders nothing, same
          "no display for absent info" posture as the rest of this view. */}
      {!isUnknown(identity.isDemo) && identity.isDemo ? (
        <Alert
          tone="warning"
          icon={<AlertTriangle className="size-5 text-nc-warning" aria-hidden />}
        >
          <AlertTitle>Demo device</AlertTitle>
          <AlertDescription>
            This device is registered in the demo registry, not production.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* ── Identity layer (always shown) ── */}
      <NCStatTileGrid title="Device identity">
        <NCStatTile>
          <Field label="Serial no" value={identity.serialNo} mono copyable />
        </NCStatTile>
        <NCStatTile>
          <Field label="Name" value={identity.name} copyable />
        </NCStatTile>
        <NCStatTile>
          <Field label="Software ID" value={identity.softwareId} mono copyable />
        </NCStatTile>
        <NCStatTile>
          <Field label="Hardware ID" value={identity.hardwareId} mono copyable />
        </NCStatTile>
      </NCStatTileGrid>

      {/* ── Operation layers, or the honest "no operation" empty state ── */}
      {operation ? (
        <div className="flex flex-col gap-nc-3 print:block">
          <NCStatTileGrid title="Firmware details" gridClassName="sm:grid-cols-3">
            <NCStatTile>
              <DeploymentChannelField value={operation.meta.deploymentChannel} />
            </NCStatTile>
            <NCStatTile>
              <Field label="Method" value={operation.meta.method} />
            </NCStatTile>
            <NCStatTile>
              <Field label="Release" value={operation.meta.release} />
            </NCStatTile>
            <NCStatTile>
              <Field label="Draft solution" value={operation.meta.draftSolution} />
            </NCStatTile>
            <NCStatTile>
              <Field label="Produced by" value={operation.meta.operatedBy} />
            </NCStatTile>
            <NCStatTile>
              <Field
                label="Production time"
                value={formatUnknownableTimestamp(operation.meta.operationTime)}
              />
            </NCStatTile>
          </NCStatTileGrid>

          <NCStatTileGrid title="Execution" gridClassName="sm:grid-cols-3">
            <NCStatTile>
              <ExecutionStatusField value={operation.execution.executionStatus} />
            </NCStatTile>
            {!isFullyExecuted(operation.execution.executionStatus) ? (
              <>
                <NCStatTile>
                  <WriteStartedField value={operation.execution.writeStarted} />
                </NCStatTile>
                <NCStatTile>
                  <FaultOriginField value={operation.execution.faultOrigin} />
                </NCStatTile>
              </>
            ) : null}
            <NCStatTile>
              <Field label="Executed by" value={operation.execution.executedBy} />
            </NCStatTile>
            <NCStatTile>
              <Field
                label="Execution time"
                value={formatUnknownableTimestamp(operation.execution.executionTime)}
              />
            </NCStatTile>
          </NCStatTileGrid>
        </div>
      ) : offlineWinner ? (
        <div className="flex flex-col gap-nc-3 print:block">
          <div className="flex flex-col gap-nc-1">
            <h3 className="text-nc-2 font-nc-2 leading-nc-2 text-foreground">
              Last update operation
            </h3>
            <p className="text-nc-1 font-nc-1 leading-nc-2 text-muted-foreground">
              Detected from the offline update log — recorded values, shown as
              records, not proof the update ran.
            </p>
          </div>

          <NCStatTileGrid title="Offline log source" gridClassName="sm:grid-cols-3">
            <NCStatTile>
              <Field label="Source file" value={offlineWinner.sourceFileName} mono />
            </NCStatTile>
            <NCStatTile>
              <Field label="Registered by" value={offlineWinner.transferredBy} />
            </NCStatTile>
            <NCStatTile>
              <Field
                label="Registered time"
                value={formatUnknownableTimestamp(offlineWinner.transferTime)}
              />
            </NCStatTile>
            <NCStatTile className="col-span-full">
              <OfflineAttributionHistory rows={offlineAttributionList ?? []} />
            </NCStatTile>
          </NCStatTileGrid>

          <OfflineAugment rows={offlineAugment ?? []} />
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-nc-3 py-10 text-center">
            <History className="size-8 shrink-0 text-muted-foreground/50" aria-hidden />
            <div className="flex flex-col gap-0.5">
              <p className="text-nc-2 font-nc-2 leading-nc-2 text-foreground">
                No update operations
              </p>
              <p className="text-nc-1 font-nc-1 leading-nc-2 text-muted-foreground">
                No firmware update has been recorded for this device yet.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Recorded `deploymentChannel` (the producing/updater AU's full name, ADR-004
 * D4), called out with a connection-type icon in the tile's top-right corner,
 * directly opposite the label, so the two currently-evidenced channels read
 * at a glance without disturbing the value text's own size/weight (matches
 * `Field`'s plain value styling exactly). The value itself is still exactly
 * what was recorded, unchanged; only known, exact literals get an icon — an
 * unrecognized channel name still renders in full, with no icon, same
 * "display heuristic over free text, not a closed enum" posture as
 * `executionStatusPresentation` below.
 */
function DeploymentChannelField({ value }: { value: Unknownable<string> }) {
  const unknown = isUnknown(value);
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-nc-2">
        <span className="text-nc-1 leading-nc-2 text-muted-foreground">Deployment channel</span>
        {!unknown ? <DeploymentChannelIcon value={value as string} /> : null}
      </div>
      <span
        className={cn(
          "break-words text-nc-1 font-nc-1 leading-nc-2",
          unknown ? "italic text-muted-foreground" : "text-foreground",
        )}
      >
        {unknown ? "unknown" : (value as string)}
      </span>
    </div>
  );
}

function DeploymentChannelIcon({ value }: { value: string }) {
  const v = value.toLowerCase();
  // `text-[var(--nc-color-secondary-2)]`, not a stock Tailwind color:
  // secondary-2 is a token mirror (globals.css), not exposed as its own
  // `@theme inline` Tailwind color utility, so it's referenced by CSS var
  // directly here rather than introducing a new token-facing utility for
  // one icon.
  if (v === "update firmware over ble") {
    return <Bluetooth className="size-3.5 shrink-0 text-[var(--nc-color-secondary-2)]" aria-hidden />;
  }
  if (v === "update firmware over serial") {
    return <Cable className="size-3.5 shrink-0 text-[var(--nc-color-secondary-2)]" aria-hidden />;
  }
  return null;
}

/**
 * Whether the recorded `upload_status` is the terminal, verified-running
 * `Executed` state. `Write started` and `Fault origin` are diagnostic
 * detail for the in-between/interrupted cases (Triggered, Flashed, or an
 * unrecognized value) — once execution is confirmed there is nothing left
 * to diagnose, so those two tiles are withheld rather than shown as
 * unconditional noise. An unknown/missing status is treated as
 * not-yet-executed (diagnostics stay visible) rather than assumed complete.
 */
function isFullyExecuted(value: Unknownable<string>): boolean {
  return !isUnknown(value) && (value as string).toLowerCase() === "executed";
}

/**
 * Recorded execution status, called out with a tone-colored icon + label
 * (the "Number + status" tile shape's status half — nc-components/usage-notes/
 * stat-tile-grid.md, "Tile content") instead of plain text, so the outcome
 * reads at a glance instead of blending into the surrounding fields. The
 * value itself is still exactly what was recorded — ADR-004's "shown as
 * records, not proof" still applies; this only changes how prominently that
 * record reads on screen. State is conveyed by icon + text together, never
 * color alone (primitive-components/usage-notes/badge.md's accessibility
 * rule, applied here to a plain tone-colored label instead of a Badge).
 *
 * The raw value is the updater AU's `upload_status` (serial/BLE progression:
 * Triggered → Flashed → Executed — the operation was triggered, the firmware
 * was written to flash but not yet verified running, then verified running).
 * Those three literals get a dedicated icon/tone each; any other recorded
 * text still renders in full, via the generic heuristic below, in a neutral
 * tone — this is a display heuristic over free text, not a closed enum.
 */
function ExecutionStatusField({ value }: { value: Unknownable<string> }) {
  const unknown = isUnknown(value);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-nc-1 leading-nc-2 text-muted-foreground">Upload status</span>
      {unknown ? (
        <span className="text-nc-1 font-nc-1 leading-nc-1 italic text-muted-foreground">
          unknown
        </span>
      ) : (
        <ExecutionStatusValue value={value as string} />
      )}
    </div>
  );
}

function ExecutionStatusValue({ value }: { value: string }) {
  const { tone, icon } = executionStatusPresentation(value);
  return (
    <span className={cn("flex items-center gap-1 text-nc-1 font-nc-1 leading-nc-1", tone)}>
      {icon}
      {value}
    </span>
  );
}

function executionStatusPresentation(value: string): { tone: string; icon: ReactNode } {
  const v = value.toLowerCase();
  // Serial/BLE's own three-stage progression (upload_status), checked first
  // and exactly, ahead of the generic free-text heuristic below.
  if (v === "triggered") {
    return { tone: "text-muted-foreground", icon: <Circle className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (v === "flashed") {
    return { tone: "text-nc-info", icon: <UploadCloud className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (v === "executed") {
    return { tone: "text-nc-success", icon: <CheckCircle2 className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (/(success|complete|done|^ok$)/.test(v)) {
    return { tone: "text-nc-success", icon: <CheckCircle2 className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (/(fail|error|reject)/.test(v)) {
    return { tone: "text-destructive", icon: <XCircle className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (/(pending|progress|running|queued)/.test(v)) {
    return { tone: "text-nc-info", icon: <Clock className="size-3.5 shrink-0" aria-hidden /> };
  }
  if (/(cancel|abort|skip)/.test(v)) {
    return { tone: "text-nc-warning", icon: <MinusCircle className="size-3.5 shrink-0" aria-hidden /> };
  }
  return { tone: "text-muted-foreground", icon: <Circle className="size-3.5 shrink-0" aria-hidden /> };
}

/**
 * Recorded `write_started` flag — whether the write/flash step was itself
 * begun, independent of how far `upload_status` progressed (a device can
 * report `Triggered` with `write_started: true` if the write began but the
 * flash step itself hasn't been confirmed yet). Same icon+text convention as
 * `ExecutionStatusField` above; the boolean is shown as a plain yes/no
 * record, not a pass/fail judgement.
 */
function WriteStartedField({ value }: { value: Unknownable<boolean> }) {
  const unknown = isUnknown(value);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-nc-1 leading-nc-2 text-muted-foreground">Write started</span>
      {unknown ? (
        <span className="text-nc-1 font-nc-1 leading-nc-1 italic text-muted-foreground">
          unknown
        </span>
      ) : (
        <span
          className={cn(
            "flex items-center gap-1 text-nc-1 font-nc-1 leading-nc-1",
            value ? "text-nc-success" : "text-muted-foreground",
          )}
        >
          {value ? (
            <CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
          ) : (
            <Circle className="size-3.5 shrink-0" aria-hidden />
          )}
          {value ? "Yes" : "No"}
        </span>
      )}
    </div>
  );
}

/**
 * Recorded `fault_origin` — set only when the write/upload was interrupted;
 * attributes the interruption to this AU's own internal fault (`AU`), the
 * device's own internal system (`device`), the `user`, or a broader
 * `systemic` failure. UNKNOWN means no fault was recorded (the common case),
 * shown as a plain "None" rather than italicized "unknown" — an absent fault
 * is not a missing field, it is the recorded absence of one.
 */
function FaultOriginField({ value }: { value: Unknownable<string> }) {
  const unknown = isUnknown(value);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-nc-1 leading-nc-2 text-muted-foreground">Fault origin</span>
      {unknown ? (
        <span className="text-nc-1 font-nc-1 leading-nc-1 text-muted-foreground">None</span>
      ) : (
        <span className="flex items-center gap-1 text-nc-1 font-nc-1 leading-nc-1 text-destructive">
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
          {faultOriginLabel(value as string)}
        </span>
      )}
    </div>
  );
}

function faultOriginLabel(value: string): string {
  switch (value.toLowerCase()) {
    case "au":
      return "AU (internal fault)";
    case "device":
      return "Device";
    case "user":
      return "User";
    case "systemic":
      return "Systemic";
    default:
      // Not a closed enum — an unrecognized recorded value still renders in
      // full rather than being silently swallowed.
      return value;
  }
}

/**
 * AU0001's `attribution_list` — this device's fuller offline attribution
 * history, rendered as a full-width tile inside the same "Offline log
 * source" panel as the source-file/transferred-by/transfer-time fields
 * above it, so the operator sees the most consistent picture of offline
 * activity for this device in one box. Provisional local display addition
 * (agreed 2026-08-10), pending a formal ADR-004 amendment to bring
 * `attribution_list` into scope (see OfflineAttributionEntry in
 * ../contracts/display). Advisory only — same "record, not proof" posture
 * as `OfflineAugment` below, and NOT part of D3 selection (only `latest`
 * competes there).
 */
function OfflineAttributionHistory({ rows }: { rows: readonly OfflineAttributionEntry[] }) {
  return (
    <div className="flex flex-col gap-nc-2 print:block">
      <div className="flex items-center gap-nc-2">
        <p className="text-nc-1 font-nc-2 leading-nc-2 text-muted-foreground">
          Attribution history
        </p>
        <Badge tone="neutral">Advisory · self-reported</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-nc-2 font-nc-1 leading-nc-2 text-muted-foreground">
          No attribution history recorded for this device.
        </p>
      ) : (
        <ul className="flex flex-col gap-nc-1 print:block">
          {rows.map((row, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-nc-3 rounded-md border border-border bg-card px-nc-3 py-nc-2 text-nc-2 font-nc-1 leading-nc-2"
            >
              <UnknownableExecutionStatusValue value={row.executionStatus} />
              <span className="text-muted-foreground">
                {isUnknown(row.operationTs) ? "unknown" : formatTimestamp(row.operationTs)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * `ExecutionStatusValue`'s unknown-tolerant counterpart, for `Unknownable`
 * `attribution_list` fields — same icon+tone convention, italic "unknown"
 * when the recorded status is absent instead of assuming a tone.
 */
function UnknownableExecutionStatusValue({ value }: { value: Unknownable<string> }) {
  if (isUnknown(value)) {
    return (
      <span className="text-nc-2 font-nc-1 leading-nc-1 italic text-muted-foreground">
        unknown
      </span>
    );
  }
  return <ExecutionStatusValue value={value} />;
}

/**
 * ADR-004 D6 offline augment (redefined by #35; #27): FirmwareDeliveryAPIv1's
 * last-3 bundle-creation records, shown beneath the AU0001-sourced winner's
 * own fields — advisory, self-reported, NOT proof of execution.
 */
function OfflineAugment({ rows }: { rows: readonly OfflineBundleRecord[] }) {
  return (
    <section className="flex flex-col gap-nc-2 print:block">
      <div className="flex items-center gap-nc-2">
        <p className="text-nc-1 font-nc-2 leading-nc-2 text-muted-foreground">
          Recent offline bundle creations
        </p>
        <Badge tone="neutral">Advisory · self-reported</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-nc-2 font-nc-1 leading-nc-2 text-muted-foreground">
          No recent offline bundle-creation records for this device.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-nc-2 font-nc-1 leading-nc-2">
            <thead>
              <tr className="border-b border-border bg-card">
                <th className="px-nc-3 py-nc-2 text-left text-nc-1 font-nc-2 text-muted-foreground">
                  Release
                </th>
                <th className="px-nc-3 py-nc-2 text-left text-nc-1 font-nc-2 text-muted-foreground">
                  Operation time
                </th>
                <th className="px-nc-3 py-nc-2 text-left text-nc-1 font-nc-2 text-muted-foreground">
                  Operated by
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const release = isUnknown(row.release) ? "unknown" : row.release;
                const operationTime = isUnknown(row.operationTime)
                  ? "unknown"
                  : formatTimestamp(row.operationTime);
                const operatedBy = isUnknown(row.operatedBy)
                  ? "unknown"
                  : row.operatedBy;
                return (
                  <tr key={`${release}-${i}`} className="border-b border-border last:border-0">
                    <td className="px-nc-3 py-nc-2">
                      <Badge tone="neutral">{release}</Badge>
                    </td>
                    <td className="px-nc-3 py-nc-2 text-muted-foreground">{operationTime}</td>
                    <td className="px-nc-3 py-nc-2 text-muted-foreground">{operatedBy}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

